import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { listLeads } from '../lib/api';
import { STAGES, countdown, formatPhone, friendlyError, isOverdue, relative, sourceLabel, stageLabel, stageTone } from '../lib/format';
import type { Lead, Stage } from '../lib/types';
import { Badge, Button, Empty, ErrorText, Notice, PageHeader } from '../components/ui';
import { AddLeadModal } from './AddLeadModal';
import { PortalImportModal } from './PortalImportModal';
import { LeadDrawer } from './LeadDrawer';

const OPEN: Stage[] = ['new', 'qualified', 'visit_booked', 'visit_done', 'negotiation'];

export function Leads() {
  const { members, projects, isManager, me } = useAuth();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [params, setParams] = useSearchParams();
  const openId = params.get('lead');
  const [stage, setStage] = useState<'open' | 'all' | Stage>('open');
  const [owner, setOwner] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [showTest, setShowTest] = useState(false);

  const load = useCallback(async () => {
    try { setLeads(await listLeads()); setError(''); } catch (e) { setError(friendlyError(e)); } finally { setLoaded(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const nameOf = (id: string | null) => members.find((m) => m.id === id)?.full_name ?? 'Unassigned';
  const projectOf = (id: string | null) => projects.find((p) => p.id === id)?.name ?? '—';

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase().replace(/\s/g, '');
    const list = leads.filter((l) => {
      if (l.is_test && !showTest) return false;
      if (stage === 'open' && !OPEN.includes(l.stage)) return false;
      if (stage !== 'open' && stage !== 'all' && l.stage !== stage) return false;
      if (owner === 'none' && l.owner_id) return false;
      if (owner !== 'all' && owner !== 'none' && l.owner_id !== owner) return false;
      if (q && !(`${l.buyer?.name ?? ''}`.toLowerCase().replace(/\s/g, '').includes(q) || (l.buyer?.phone_e164 ?? '').includes(q) || `l-${l.ref_no}`.includes(q))) return false;
      return true;
    });
    return [...list.filter((l) => l.needs_human_at), ...list.filter((l) => !l.needs_human_at)];
  }, [leads, stage, owner, search, showTest]);

  const openLead = (id: string) => setParams((p) => { p.set('lead', id); return p; });
  const closeLead = useCallback(() => setParams((p) => { p.delete('lead'); return p; }), [setParams]);

  return (
    <div>
      <PageHeader
        title={isManager ? 'Leads' : 'My leads'}
        subtitle={loaded ? `${shown.length} shown of ${leads.filter((l) => showTest || !l.is_test).length}` : 'Loading…'}
        action={<div className="flex flex-wrap gap-2">
          {isManager && <Button onClick={() => { setNotice(''); setImporting(true); }}>Import from portal</Button>}
          <Button variant="primary" onClick={() => { setNotice(''); setAdding(true); }}>Add lead</Button>
        </div>}
      />

      {notice && <div className="mb-4"><Notice>{notice}</Notice></div>}
      <ErrorText>{error}</ErrorText>

      <div className="mb-4 flex flex-wrap gap-2">
        <input aria-label="Search leads" placeholder="Search name, phone or L-number" value={search} onChange={(e) => setSearch(e.target.value)}
          className="min-w-0 flex-1 rounded-lg border border-line px-3 py-2 text-sm outline-none focus:border-accent sm:max-w-[320px]" />
        <select aria-label="Stage" value={stage} onChange={(e) => setStage(e.target.value as typeof stage)} className="rounded-lg border border-line px-3 py-2 text-sm">
          <option value="open">Open leads</option>
          <option value="all">All stages</option>
          {STAGES.map((s) => <option key={s} value={s}>{stageLabel[s]}</option>)}
        </select>
        {leads.some((l) => l.is_test) && (
          <label className="flex items-center gap-2 rounded-lg border border-line bg-surface px-3 py-2 text-sm">
            <input type="checkbox" checked={showTest} onChange={(e) => setShowTest(e.target.checked)} /> Show test leads
          </label>
        )}
        {isManager && (
          <select aria-label="Owner" value={owner} onChange={(e) => setOwner(e.target.value)} className="rounded-lg border border-line px-3 py-2 text-sm">
            <option value="all">Everyone</option>
            <option value="none">Unassigned</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.id === me?.id ? `${m.full_name} (me)` : m.full_name}</option>)}
          </select>
        )}
      </div>

      {loaded && leads.length === 0 && (
        <Empty title="No leads yet">Add one by hand now. WhatsApp enquiries will land here automatically from week 2.</Empty>
      )}
      {loaded && leads.length > 0 && shown.length === 0 && <Empty title="Nothing matches these filters" />}

      {shown.length > 0 && (
        <ul className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-surface">
          {shown.map((l) => {
            const overdue = isOverdue(l.next_step_at);
            return (
              <li key={l.id}>
                <button onClick={() => openLead(l.id)} className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 px-4 py-3 text-left hover:bg-surface2 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)_auto] md:items-center">
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{l.buyer?.name || formatPhone(l.buyer?.phone_e164 ?? '')}</span>
                    <span className="block truncate text-xs text-muted">
                      <span className="font-mono">L-{l.ref_no}</span> · {l.buyer?.name ? formatPhone(l.buyer.phone_e164) : sourceLabel[l.source]}
                      {l.buyer?.is_nri ? ' · NRI' : ''}{l.is_test ? ' · Test' : ''}
                    </span>
                  </span>
                  <span className="flex gap-1 justify-self-end md:order-last">
                    {l.needs_human_at && <Badge tone="bad">Needs you</Badge>}
                    <Badge tone={stageTone[l.stage]}>{stageLabel[l.stage]}</Badge>
                  </span>
                  <span className="truncate text-sm text-muted md:text-ink">{projectOf(l.project_id)} · {nameOf(l.owner_id)}</span>
                  <span className={`truncate text-right text-sm md:text-left ${overdue ? 'text-bad' : 'text-muted'}`}>
                    {l.response_due_at ? countdown(l.response_due_at)!.text : l.next_step_at ? `${overdue ? 'Overdue · ' : ''}${relative(l.next_step_at)}` : l.owner_id ? 'No next step' : 'Unassigned'}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <AddLeadModal open={adding} onClose={() => setAdding(false)}
        onAdded={async (id, msg) => { setAdding(false); setNotice(msg); await load(); if (id) openLead(id); }} />
      {importing && <PortalImportModal onClose={() => setImporting(false)}
        onImported={async (id, msg) => { setImporting(false); setNotice(msg); await load(); openLead(id); }} />}
      {openId && <LeadDrawer leadId={openId} onClose={closeLead} onChanged={load} />}
    </div>
  );
}
